import { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { Card, Section, TH, TD, Empty, Badge, Button } from "../components/ui";
import { toast } from "sonner";

const REASONS = {
  cif_missing: "CIF/NIF mancante",
  invalid_fiscal_id: "CIF/NIF non valido",
  fd_api_error: "Errore FacturaDirecta",
  tax_unmapped: "Imposta non mappata",
  total_mismatch: "Totale non coerente",
  missing_original_invoice: "Fattura originaria mancante",
  modified_after_export: "Modificata dopo export",
  contact_not_synced: "Cliente non sincronizzato",
  rectification_review: "Conferma rettificativa",
};

export default function ReviewQueuePage() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState({});
  const [filter, setFilter] = useState({ company_id: "", entity: "", status: "open" });

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) params.append(k, v); });
    const [r, c] = await Promise.all([api.get(`/review-queue?${params}`), api.get("/companies")]);
    setItems(r.data);
    setCompanies(Object.fromEntries(c.data.map((x) => [x.id, x.name])));
  };
  useEffect(() => { load(); }, [filter]);

  const act = async (id, action) => {
    try { await api.post(`/review-queue/${id}/action`, { action }); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      <Section title="Coda Revisione" subtitle="Elementi da risolvere manualmente prima della sincronizzazione">
        <div className="flex gap-3 mb-3">
          <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                  value={filter.company_id} onChange={(e) => setFilter({ ...filter, company_id: e.target.value })}>
            <option value="">Tutte le società</option>
            {Object.entries(companies).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                  value={filter.entity} onChange={(e) => setFilter({ ...filter, entity: e.target.value })}>
            <option value="">Tutte le entità</option>
            <option value="contact">Clienti</option>
            <option value="invoice">Fatture</option>
            <option value="credit_note">Note credito</option>
          </select>
          <select className="h-9 px-3 rounded-md border border-border-default text-sm bg-surface"
                  value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="select-review-status">
            <option value="open">Aperti</option>
            <option value="resolved">Risolti</option>
            <option value="ignored">Ignorati</option>
            <option value="retry">Da rilanciare</option>
            <option value="">Tutti</option>
          </select>
        </div>
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-hover/50 border-b border-border-default">
              <tr><TH>Società</TH><TH>Entità</TH><TH>Zoho ID</TH><TH>Motivo</TH><TH>Dettaglio</TH><TH>Stato</TH><TH>Azioni</TH></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <ReviewRow key={it.id} it={it} companyName={companies[it.company_id]} onAction={act} />
              ))}
              {items.length === 0 && <tr><td colSpan={7}><Empty>Nessun elemento.</Empty></td></tr>}
            </tbody>
          </table>
        </Card>
      </Section>
    </div>
  );
}

function ReviewRow({ it, companyName, onAction }) {
  const [open, setOpen] = useState(false);
  const hasDetails = Boolean(it.details) || Boolean(it.payload);
  return (
    <>
      <tr className="border-b border-border-divider">
        <TD>{companyName || it.company_id.slice(0, 8)}</TD>
        <TD>{it.entity}</TD>
        <TD className="font-mono text-xs">{it.zoho_id || "—"}</TD>
        <TD><Badge tone="warning">{REASONS[it.reason] || it.reason}</Badge></TD>
        <TD className="text-xs text-ink-muted max-w-sm">
          <div className="flex items-center gap-2">
            <span className="truncate" title={it.details || ""}>{it.details || "—"}</span>
            {hasDetails && (
              <button
                className="text-xs underline text-ink hover:text-ink/80 shrink-0"
                onClick={() => setOpen((v) => !v)}
                data-testid={`btn-expand-review-${it.id}`}
              >
                {open ? "Nascondi" : "Apri"}
              </button>
            )}
          </div>
        </TD>
        <TD><Badge>{it.status}</Badge></TD>
        <TD>
          {it.status === "open" && (
            <div className="flex gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => onAction(it.id, "resolved")} data-testid={`btn-resolve-${it.id}`}>Risolto</Button>
              <Button size="sm" variant="ghost" onClick={() => onAction(it.id, "ignored")} data-testid={`btn-ignore-${it.id}`}>Ignora</Button>
              <Button size="sm" variant="ghost" onClick={() => onAction(it.id, "retry")} data-testid={`btn-retry-${it.id}`}>Riprova</Button>
            </div>
          )}
        </TD>
      </tr>
      {open && hasDetails && (
        <tr className="border-b border-border-divider bg-surface-hover/30">
          <td colSpan={7} className="p-4">
            {it.details && (
              <div className="mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">Dettaglio completo</div>
                <pre className="font-mono text-xs whitespace-pre-wrap break-all bg-app rounded p-3 border border-border-default select-all">{it.details}</pre>
              </div>
            )}
            {it.payload && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">Payload</div>
                <pre className="font-mono text-xs whitespace-pre-wrap break-all bg-app rounded p-3 border border-border-default select-all">{JSON.stringify(it.payload, null, 2)}</pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
